import { useEffect } from "react";

import { objectTypes, actionNames } from "@newsela/snitch";
import { Anchor } from "@newsela/angelou";
import { useRouter } from "next/router";
import PropTypes from "prop-types";

import InfoIcon from "@/icons/InfoIcon";
import focusElementRefKeys from "@/view/constants/focusElementRefKeys";
import useAnnotationsState from "@/view/utils/useAnnotationsState";
import useFocusManager from "@/view/utils/useFocusManager";
import {
  panelWrapperCss,
  scrollContainerCss,
} from "../ActivityPanel/ActivityPanel.style";
import PanelHeader from "../PanelHeader";
import AnnotationCard from "./AnnotationCard";
import {
  anchorCss,
  iconCss,
  messageContainerCss,
  panelBodyCss,
  textCss,
} from "./AnnotationContainer.style";

const propTypes = {
  closeButtonEventProperties: PropTypes.object,
  shouldFocusCloseButtonRef: PropTypes.object,
  legacyArticleId: PropTypes.number,
  onClickCloseButton: PropTypes.func,
  userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  userProperties: PropTypes.object,
};

const NoAnnotationsMessage = () => {
  return (
    <div css={messageContainerCss}>
      <InfoIcon css={iconCss} />
      <div>
        <p css={textCss}>
          Highlight part of the article’s text to add a comment at this Lexile
          level.
        </p>
        <Anchor
          __cssFor={{
            root: anchorCss,
          }}
          href="https://support.newsela.com/article/accessibility-on-newsela/#annotations"
          isInline
          openInNewTab
        >
          Learn more about annotations
        </Anchor>
      </div>
    </div>
  );
};

const AnnotationsContainer = ({
  closeButtonEventProperties,
  onClickCloseButton,
  userId,
  userProperties,
  legacyArticleId,
  shouldFocusCloseButtonRef,
}) => {
  const {
    annotationCardRefs,
    annotationComments,
    draftAnnotation,
    focusContentBodyAnnotation,
    getAnnotations,
    hasNoAnnotations,
    highlightedText,
    newlySavedAnnotationId,
    orderedListOfAnnotations,
    setDraftAnnotation,
    setNewlySavedAnnotationId,
    updateAnnotationForReadPage,
  } = useAnnotationsState();
  const router = useRouter();
  const {
    query: { levelId },
  } = router;

  const backButtonEventProperties = {
    objectType: objectTypes.annotation,
    actionName: actionNames.navigate,
    streamProperties: {
      content_type: "Article",
      description: "Navigate back to Activity Picker from annotations",
      content_id: levelId,
    },
  };

  const onClickBackButton = () => {
    if (draftAnnotation) {
      setDraftAnnotation(null);
    }
    const query = { ...router.query };
    delete query.activityPanel;
    router.replace({ pathname: router.pathname, query }, null, {
      shallow: true,
    });
  };

  const handleClose = () => {
    if (draftAnnotation) {
      setDraftAnnotation(null);
    }

    onClickCloseButton();
  };

  const { focusElement } = useFocusManager();

  useEffect(() => {
    // When there is no draft annotation card to display, move focus to the back button in the panel.
    if (!draftAnnotation) {
      focusElement(focusElementRefKeys.activityPanelBackButton);
    }
  }, []);

  const renderAnnotationCard = (annotation) => {
    const isDraft = annotation.id === 0;
    const annotationId = isDraft ? draftAnnotation.id : annotation.id;

    return (
      <AnnotationCard
        key={annotation.id}
        annotation={isDraft ? draftAnnotation : annotation}
        annotationCardRefs={annotationCardRefs}
        comments={annotationComments}
        focusContentBodyAnnotation={focusContentBodyAnnotation}
        getAnnotations={getAnnotations}
        highlightedText={highlightedText[annotationId]}
        isDraft={isDraft}
        newlySavedAnnotationId={newlySavedAnnotationId}
        setDraftAnnotation={setDraftAnnotation}
        setNewlySavedAnnotationId={setNewlySavedAnnotationId}
        updateAnnotationForReadPage={updateAnnotationForReadPage}
        userId={userId}
        userProperties={userProperties}
        legacyArticleId={legacyArticleId}
      />
    );
  };

  return (
    <div css={panelWrapperCss}>
      <PanelHeader
        onClickBackButton={onClickBackButton}
        onClickCloseButton={handleClose}
        closeButtonEventProperties={closeButtonEventProperties}
        backButtonEventProperties={backButtonEventProperties}
        shouldFocusCloseButtonRef={shouldFocusCloseButtonRef}
      >
        <h2>Annotations</h2>
      </PanelHeader>
      <div css={[panelBodyCss, scrollContainerCss]}>
        {hasNoAnnotations ? (
          <NoAnnotationsMessage />
        ) : (
          orderedListOfAnnotations?.map((annotation) => {
            return renderAnnotationCard(annotation);
          })
        )}
      </div>
    </div>
  );
};

AnnotationsContainer.propTypes = propTypes;

export default AnnotationsContainer;
